import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { MessageBubble } from './MessageBubble';
import type { ChatMessage } from './messages';
import { prefersReducedMotion } from './typewriter';

export interface MessageListProps {
  messages: ChatMessage[];
  composerRef?: RefObject<HTMLElement>;
  /** shown above the first message (the widget's Greeting) */
  greeting?: ReactNode;
}

export function MessageList({ messages, composerRef, greeting }: MessageListProps): JSX.Element {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const [glide] = useState(createGlide);
  // Set once the reader taps, clicks or scrolls inside the conversation. From then
  // on, nothing moves their view (a card's format list, "Pourquoi ce produit", the
  // rest of a streamed reply); their next question hands control back.
  const readerTookOver = useRef(false);
  const lastQuestion = useRef<string | undefined>();

  const freshChat = messages.every((message) => message.type === 'welcome');

  useLayoutEffect(() => {
    // A streamed reply changes `messages` with every line: only a new question counts.
    const question = messages.filter((message) => message.type === 'user').pop()?.id;
    if (question !== lastQuestion.current) {
      lastQuestion.current = question;
      readerTookOver.current = false;
    }
    if (freshChat) return;
    const positionLatestMessage = () => {
      if (!readerTookOver.current) positionMessage(messages.length, composerRef, endRef.current, glide);
    };

    positionLatestMessage();

    // The message state can commit before markdown, fonts, or media finish
    // affecting layout. Re-check on the next frame so the final rendered
    // bottom, rather than the pre-layout height, is visible.
    if (typeof globalThis.requestAnimationFrame !== 'function') return;
    const frame = globalThis.requestAnimationFrame(positionLatestMessage);
    return () => globalThis.cancelAnimationFrame(frame);
  }, [composerRef, messages, freshChat, glide]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const ResizeObserverConstructor = globalThis.ResizeObserver;
    if (!content || typeof ResizeObserverConstructor === 'undefined') return;

    // Long streamed replies and late font/media layout can grow without a
    // new messages array. Re-evaluate whether its beginning or end should
    // remain visible after each size change.
    const observer = new ResizeObserverConstructor(() => {
      if (readerTookOver.current || freshChat) return;
      positionMessage(messages.length, composerRef, endRef.current, glide);
    });
    observer.observe(content);
    if (composerRef?.current) observer.observe(composerRef.current);
    return () => observer.disconnect();
  }, [composerRef, messages.length, freshChat, glide]);

  useLayoutEffect(() => glide.stop, [glide]);

  const takeOver = () => {
    readerTookOver.current = true;
    glide.stop();
  };

  return (
    <div
      role="log"
      aria-live="polite"
      className="tw-bg-white"
      onPointerDown={takeOver}
      onWheel={takeOver}
    >
      <div ref={contentRef} className="tw-max-w-[800px] tw-mx-auto tw-px-2 tw-py-3 tw-space-y-3">
        {greeting}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        <div ref={endRef} aria-hidden="true" data-chat-scroll-end="" />
      </div>
    </div>
  );
}

type Glide = ReturnType<typeof createGlide>;

function positionMessage(
  messageCount: number,
  composerRef: RefObject<HTMLElement> | undefined,
  end: HTMLDivElement | null,
  glide: Glide,
): void {
  if (!end || messageCount === 0) return;

  const latestMessage = end.previousElementSibling as HTMLElement | null;
  if (!latestMessage) return;

  const composerHeight = Math.ceil(composerRef?.current?.getBoundingClientRect().height ?? 0);
  const viewBottom = Math.max(0, globalThis.innerHeight - composerHeight);
  const message = latestMessage.getBoundingClientRect();

  // Too tall for the space above the sticky composer: show where it begins. Otherwise
  // keep it whole, its end just above the composer.
  const offset = message.height > viewBottom ? message.top : end.getBoundingClientRect().bottom - viewBottom;
  glide.to(globalThis.scrollY + offset);
}

/** Time constant of the glide: ~90% of the way in 100ms, settled in about 250ms. */
const GLIDE_MS = 45;

/**
 * Scrolls the page (the chat sits in the document flow) to a position, fast but eased
 * rather than in one jump. A new target mid-way only changes course, so a reply that keeps
 * growing doesn't restart it.
 */
function createGlide() {
  let target = 0;
  let frame = 0;
  let last = 0;
  // `instant`, so a host page's `scroll-behavior: smooth` doesn't slow every step down
  const scrollTo = (top: number) => globalThis.scrollTo({ top, behavior: 'instant' });

  const step = (now: number) => {
    const from = globalThis.scrollY;
    const left = target - from;
    const fraction = 1 - Math.exp(-Math.max(now - last, 1) / GLIDE_MS);
    last = now;
    const next = Math.abs(left) <= 1 ? target : from + Math.sign(left) * Math.max(1, Math.abs(left) * fraction);
    scrollTo(next);
    // there, or held at the end of the page
    frame = next === target || globalThis.scrollY === from ? 0 : globalThis.requestAnimationFrame(step);
  };

  return {
    to(y: number) {
      target = Math.max(0, y);
      if (frame || Math.abs(target - globalThis.scrollY) < 1) return;
      if (typeof globalThis.requestAnimationFrame !== 'function' || prefersReducedMotion()) {
        scrollTo(target);
        return;
      }
      last = globalThis.performance.now() - 16;
      frame = globalThis.requestAnimationFrame(step);
    },
    stop() {
      if (frame) globalThis.cancelAnimationFrame(frame);
      frame = 0;
    },
  };
}
